import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { checkinsApi, reportsApi, locationsApi } from '../services/api'
import toast from 'react-hot-toast'
import { 
  ChartBarIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  GlobeAltIcon,
  TrophyIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import GeofenceCheckIn from '../components/GeofenceCheckIn'

interface DashboardData {
  totalCheckIns: number
  totalIncidents: number
  totalHourlyReports: number
  averageCheckInDuration: number
  recentActivity: Array<{
    id: string
    type: 'checkin' | 'checkout' | 'incident' | 'hourly_report'
    location: string
    timestamp: string
    description: string
  }>
  locationStats: Array<{
    location: string
    checkIns: number
    incidents: number
    hourlyReports: number
  }>
  weeklyTrends: Array<{
    date: string
    checkIns: number
    incidents: number
    hourlyReports: number
  }>
}

interface DashboardProps {
  user: any
}

interface RecentActivity {
  id: string
  type: 'checkin' | 'checkout' | 'incident' | 'hourly'
  location: string
  timestamp: string
  status: string
}

interface Location {
  id: string
  name: string
  address: string
  description?: string
  centerLat?: number
  centerLng?: number
  radiusMeters?: number
  geoType?: string
}

interface CheckInStatus {
  id: string
  location: string
  locationId: string
  checkInTime: string
  notes?: string
  isWithinGeofence: boolean
}

export default function Dashboard({ user }: DashboardProps) {
  console.log('🎯 DASHBOARD COMPONENT LOADED - ANALYTICS PAGE!')
  const [stats, setStats] = useState<DashboardData | null>(null)
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [currentDate] = useState(new Date())
  
  // Geofence state
  const [locations, setLocations] = useState<Location[]>([])
  const [currentCheckIn, setCurrentCheckIn] = useState<CheckInStatus | null>(null)
  const [currentLocation, setCurrentLocation] = useState<{lat: number, lng: number} | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [geofenceStatus, setGeofenceStatus] = useState<'inside' | 'outside' | null>(null)
  const [nearbyLocations, setNearbyLocations] = useState<Location[]>([])

  useEffect(() => {
    console.log('🔄 useEffect triggered - loading dashboard data...')
    
    console.log('📞 Calling loadDashboardData...')
    loadDashboardData()
    
    console.log('📞 Calling loadLocations...')
    loadLocations()
    
    console.log('📞 Calling loadCurrentCheckIn...')
    loadCurrentCheckIn()
    
    console.log('📞 Calling getCurrentLocation...')
    getCurrentLocation()
    
    console.log('✅ All functions called in useEffect')
  }, [])

  const loadDashboardData = async () => {
    console.log('📊 loadDashboardData function started')
    try {
      setLoading(true)
      
      console.log('🚀 Starting to fetch dashboard data from API...')
      
      // Check for token in the correct key (auth_token)
      const token = localStorage.getItem('auth_token')
      console.log('🔑 Token found:', token ? 'Yes' : 'No')
      
      if (!token) {
        console.log('⚠️ No token, using fallback data')
        // Use fallback data instead of throwing error
        const fallbackStats: DashboardData = {
          totalCheckIns: 0,
          totalIncidents: 0,
          totalHourlyReports: 0,
          averageCheckInDuration: 0,
          recentActivity: [],
          locationStats: [],
          weeklyTrends: []
        }
        setStats(fallbackStats)
        setRecentActivity([])
        return
      }
      
      // Fetch real dashboard data from API
      const apiUrl = '/api/app/dashboard/stats'
      console.log('📡 Fetching from:', apiUrl)
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })

      console.log('📊 Response status:', response.status)
      
      if (!response.ok) {
        console.error('❌ HTTP error:', response.status, response.statusText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('📊 API response:', result)
      const dashboardData = result.data

      // Transform API data to match expected format
      const transformedStats: DashboardData = {
        totalCheckIns: dashboardData.totalCheckIns || 0,
        totalIncidents: dashboardData.totalIncidents || 0,
        totalHourlyReports: dashboardData.totalHourlyReports || 0,
        averageCheckInDuration: dashboardData.averageShiftHours || 0,
        recentActivity: dashboardData.recentActivity || [],
        locationStats: [
          { location: 'Main Building - East Wing', checkIns: 0, incidents: 0, hourlyReports: 0 }
        ],
        weeklyTrends: [
          { 
            date: new Date().toISOString().split('T')[0], 
            checkIns: dashboardData.weeklyCheckIns || 0, 
            incidents: 0, 
            hourlyReports: 0 
          }
        ]
      }

      setStats(transformedStats)
      setRecentActivity(dashboardData.recentActivity || [])
    } catch (error) {
      console.error('❌ Failed to load dashboard data:', error)
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown'
      })
      
      // Fallback to mock data if API fails
      const fallbackStats: DashboardData = {
        totalCheckIns: 0,
        totalIncidents: 0,
        totalHourlyReports: 0,
        averageCheckInDuration: 0,
        recentActivity: [],
        locationStats: [],
        weeklyTrends: []
      }
      setStats(fallbackStats)
      setRecentActivity([])
    } finally {
      console.log('✅ Dashboard data loading completed')
      setLoading(false)
    }
  }

  const loadLocations = async () => {
    console.log('📍 loadLocations function started')
    try {
      console.log('🔍 Loading locations...')
      // Mock locations data
      const mockLocations: Location[] = [
        { id: 'loc1', name: 'Main Building - East Wing', address: '123 Main St, Suite 100', centerLat: 34.0522, centerLng: -118.2437, radiusMeters: 100 },
        { id: 'loc2', name: 'Main Building - West Wing', address: '123 Main St, Suite 200', centerLat: 34.0522, centerLng: -118.2437, radiusMeters: 100 },
        { id: 'loc3', name: 'Parking Lot', address: '123 Main St, Parking Lot', centerLat: 34.0522, centerLng: -118.2437, radiusMeters: 200 }
      ]
      setLocations(mockLocations)
      console.log('✅ Locations loaded:', mockLocations.length, 'locations')
    } catch (error) {
      console.error('❌ Failed to load locations:', error)
      toast.error('Failed to load locations')
    }
  }

  const loadCurrentCheckIn = async () => {
    console.log('📋 loadCurrentCheckIn function started')
    try {
      // Mock current check-in status
      const mockCheckIn: CheckInStatus = {
        id: 'mock-check-in-id',
        location: 'Main Building - East Wing',
        locationId: 'loc1',
        checkInTime: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
        notes: 'Mock check-in notes',
        isWithinGeofence: true
      }
      setCurrentCheckIn(mockCheckIn)
    } catch (error) {
      console.error('Failed to load current check-in:', error)
    }
  }

  const getCurrentLocation = () => {
    console.log('🌍 getCurrentLocation function started')
    setLocationLoading(true)
    setLocationError('')
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by this browser')
      setLocationLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        setLocationLoading(false)
      },
      (error) => {
        let errorMessage = 'Unable to get your location'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
        }
        setLocationError(errorMessage)
        setLocationLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    )
  }

  // Calculate geofence status when location and current position are available
  useEffect(() => {
    if (currentLocation && locations.length > 0) {
      // Find nearby locations
      const nearby = locations.filter(location => {
        if (location.centerLat && location.centerLng && location.radiusMeters) {
          const distance = calculateDistance(
            location.centerLat,
            location.centerLng,
            currentLocation.lat,
            currentLocation.lng
          )
          return distance <= location.radiusMeters
        }
        return false
      })
      setNearbyLocations(nearby)
      
      // Set geofence status based on nearby locations
      if (nearby.length > 0) {
        setGeofenceStatus('inside')
      } else {
        setGeofenceStatus('outside')
      }
    }
  }, [currentLocation, locations])

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180
    const φ2 = lat2 * Math.PI / 180
    const Δφ = (lat2 - lat1) * Math.PI / 180
    const Δλ = (lon2 - lon1) * Math.PI / 180

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distance in meters
  }

  const handleCheckInComplete = (locationId: string) => {
    // Reload current check-in status
    loadCurrentCheckIn()
    toast.success('Check-in completed successfully!')
  }

  const handleCheckOutComplete = () => {
    // Reload current check-in status
    loadCurrentCheckIn()
    toast.success('Check-out completed successfully!')
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'checkin':
        return ClockIcon
      case 'checkout':
        return ClockIcon
      case 'incident':
        return ExclamationTriangleIcon
      case 'hourly':
        return ChartBarIcon // Changed from DocumentChartBarIcon
      default:
        return ChartBarIcon // Changed from DocumentChartBarIcon
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'checkin':
        return 'text-success'
      case 'checkout':
        return 'text-warning'
      case 'incident':
        return 'text-error'
      case 'hourly':
        return 'text-secondary-500'
      default:
        return 'text-tech-400'
    }
  }

  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60)
      if (diffInMinutes <= 0) {
        return 'Just now'
      }
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours)
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else {
      const days = Math.floor(diffInHours / 24)
      return `${days} day${days > 1 ? 's' : ''} ago`
    }
  }

  const statCards = [
    {
      name: 'Total Check-ins',
      value: stats?.totalCheckIns || 0,
      icon: ClockIcon,
      gradient: 'from-success to-accent-emerald',
      description: 'Location visits'
    },
    {
      name: 'Total Incidents',
      value: stats?.totalIncidents || 0,
      icon: ExclamationTriangleIcon,
      gradient: 'from-error to-warning',
      description: 'Security incidents'
    },
    {
      name: 'Hourly Reports',
      value: stats?.totalHourlyReports || 0,
      icon: ChartBarIcon, // Changed from DocumentChartBarIcon
      gradient: 'from-secondary-500 to-secondary-600',
      description: 'Activity reports'
    },
    {
      name: 'Total Hours',
      value: stats?.averageCheckInDuration || 0, // Changed from totalHours
      icon: CalendarIcon,
      gradient: 'from-primary-500 to-primary-600',
      description: 'Hours worked'
    }
  ]

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm">
              <ChartBarIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-display-sm font-display text-gradient">Dashboard</h1>
              <p className="text-body text-gray-600 dark:text-tech-400 font-mono">Your activity overview</p>
            </div>
          </div>
        </div>

        {/* Loading skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card animate-pulse">
              <div className="h-12 w-12 bg-tech-700 rounded-xl mb-4"></div>
              <div className="h-4 bg-tech-700 rounded mb-2"></div>
              <div className="h-8 bg-tech-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm animate-pulse-orange">
              <ChartBarIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-display-sm font-display text-gradient">Dashboard</h1>
              <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
                Welcome back, {user?.name}! Your activity overview
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-body-sm text-gray-600 dark:text-tech-400">
              {currentDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'short', 
                day: 'numeric' 
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Geofence Status Banner */}
      {!currentCheckIn && currentLocation && (
        <div className="premium-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Location Status</h2>
            <button
              onClick={getCurrentLocation}
              disabled={locationLoading}
              className="p-2 text-primary-500 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-full disabled:opacity-50 transition-colors"
            >
              <GlobeAltIcon className="h-6 w-6" />
            </button>
          </div>
          
          {locationLoading ? (
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
              <span className="text-body text-gray-600 dark:text-tech-400">Getting your location...</span>
            </div>
          ) : locationError ? (
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-error/20">
                <ExclamationTriangleIcon className="w-5 h-5 text-error" />
              </div>
              <span className="text-body text-error">{locationError}</span>
            </div>
          ) : geofenceStatus === 'inside' ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-success/20">
                  <CheckCircleIcon className="w-5 h-5 text-success" />
                </div>
                <div>
                  <div className="text-body font-medium text-success">You're within a geofence!</div>
                  <div className="text-body-sm text-gray-600 dark:text-tech-400">
                    {nearbyLocations.length} location{nearbyLocations.length !== 1 ? 's' : ''} nearby
                  </div>
                </div>
              </div>
              
              {/* Quick Check-in Button */}
              <button
                onClick={() => window.location.href = '/status'}
                className="w-full flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <ClockIcon className="w-5 h-5" />
                <span className="text-body-sm font-medium">Check In Now</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/20">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="text-body font-medium text-orange-600 dark:text-orange-400">
                  You're outside all geofences
                </div>
                <div className="text-body-sm text-gray-600 dark:text-tech-400">
                  Move closer to a location or use manual check-in
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="premium-card p-6">
        <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button 
            onClick={() => window.location.href = '/status'}
            className="flex items-center space-x-3 p-4 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
          >
            <ClockIcon className="w-5 h-5" />
            <span className="text-body-sm font-medium">Check In</span>
          </button>
          <button 
            onClick={() => window.location.href = '/reports'}
            className="flex items-center space-x-3 p-4 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:-translate-y-0.5"
          >
            <ChartBarIcon className="w-5 h-5" />
            <span className="text-body-sm font-medium">View Reports</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div key={stat.name} className={`stat-card animate-slide-up delay-${index * 100}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-glow-sm`}>
                <stat.icon className="h-6 w-6 text-white stat-icon" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="stat-value">{stat.value}</div>
              <div className="text-body-sm text-gray-600 dark:text-tech-400 font-medium">{stat.name}</div>
              <div className="text-caption text-gray-700 dark:text-tech-500">{stat.description}</div>
            </div>
          </div>
        ))}
      </div>

             {/* Performance Metrics */}
       <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
         <div className="premium-card p-6">
           <div className="flex items-center justify-between mb-4">
             <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">This Week</h3>
             <div className="p-2 rounded-lg bg-gradient-to-br from-tech-700 to-tech-600">
               <CalendarIcon className="h-5 w-5 text-tech-300" />
             </div>
           </div>
           <div className="space-y-3">
             <div className="flex justify-between">
               <span className="text-body-sm text-gray-600 dark:text-tech-400">Check-ins</span>
               <span className="text-body-sm font-medium text-gray-900 dark:text-tech-50">{stats?.weeklyTrends[0]?.checkIns}</span>
             </div>
             <div className="flex justify-between">
               <span className="text-body-sm text-gray-600 dark:text-tech-400">Avg. Shift</span>
               <span className="text-body-sm font-medium text-gray-900 dark:text-tech-50">{stats?.averageCheckInDuration} hours</span>
             </div>
           </div>
         </div>
       </div>

      {/* Recent Activity */}
      <div className="premium-card p-6 lg:p-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
            <ClockIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Recent Activity</h3>
            <p className="text-body-sm text-gray-600 dark:text-tech-400">Your latest check-ins and reports</p>
          </div>
        </div>
        <div className="space-y-3">
          {recentActivity.map((activity) => {
            const ActivityIcon = getActivityIcon(activity.type)
            const colorClass = getActivityColor(activity.type)
            
            return (
              <div key={activity.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800/30 transition-colors">
                <div className={`p-2 rounded-full bg-gray-100 dark:bg-tech-800`}>
                  <ActivityIcon className={`w-4 h-4 ${colorClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-body-sm font-medium text-gray-900 dark:text-tech-50 capitalize">
                      {activity.type === 'checkin' ? 'Check In' : 
                       activity.type === 'checkout' ? 'Check Out' :
                       activity.type === 'incident' ? 'Incident Report' : 'Hourly Report'}
                    </span>
                    <span className="text-caption text-success font-medium">
                      {activity.status}
                    </span>
                  </div>
                  <p className="text-caption text-gray-600 dark:text-tech-400 truncate">
                    {activity.location}
                  </p>
                </div>
                <div className="text-caption text-gray-600 dark:text-tech-400">
                  {formatRelativeTime(activity.timestamp)}
                </div>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}